import { $api, fetchClient } from '@/api/typedClient'
import { message } from 'antd'
import type { CrossroadsLog } from './queries'

export interface RetryResult {
  success: boolean
  updatedLog?: CrossroadsLog
}

export async function retryConnection(
  tenant: string,
  log: CrossroadsLog,
  logGroupId: string,
): Promise<RetryResult> {
  try {
    const originOrderNumber =
      log.metadata?.type === 'order' ? log.metadata.origin_order_number : null

    if (!originOrderNumber) {
      message.error({
        content: 'No origin order number found',
        key: 'retry',
      })
      return { success: false }
    }

    if (!log._id) {
      message.error({
        content: 'No log ID found',
        key: 'retry',
      })
      return { success: false }
    }

    message.loading({
      content: 'Retrying operation...',
      key: 'retry',
      duration: 0,
    })

    const response = await fetchClient.POST('/crossroads/logging/retry', {
      params: {
        query: {
          origin_order_number: originOrderNumber,
          log_id: log._id,
          log_group_id: logGroupId,
        },
        header: { 'X-Tenant-Name': tenant },
      },
    })

    if (response.error) {
      const errorDetail =
        typeof response.error.detail === 'string'
          ? response.error.detail
          : 'Unknown error'
      message.error({
        content: `Failed to retry: ${errorDetail}`,
        key: 'retry',
      })
      return { success: false }
    }

    const result = response.data
    const primary_result = result.primary
    if (primary_result?.success) {
      message.success({
        content: `Retry successful! Status: ${primary_result.status}`,
        key: 'retry',
      })
      return {
        success: true,
        updatedLog: primary_result.updated_log ?? undefined,
      }
    }
    message.warning({
      content: `Retry completed with status: ${primary_result?.status}`,
      key: 'retry',
    })
    return {
      success: false,
      updatedLog: primary_result?.updated_log ?? undefined,
    }
  } catch (_error) {
    message.error({ content: 'Failed to retry', key: 'retry' })
    return { success: false }
  }
}

/**
 * The updated groups come back on the response; callers apply them to their
 * grid row. Under the server-side row model the rows live in AG Grid's store,
 * not in a react-query cache, so there is nothing to patch here.
 */
export function useSetOrderSilencing() {
  return $api.useMutation('post', '/crossroads/logging/set_order_silencing', {
    onError: (error) => {
      message.error(`Failed to update silencing: ${JSON.stringify(error)}`)
    },
  })
}

export function useLatestSagaEvent() {
  return $api.useMutation('post', '/crossroads/logging/latest_saga_event', {
    onError: (error) => {
      message.error(`Failed to load event: ${JSON.stringify(error)}`)
    },
  })
}

export function useBulkRetry() {
  return $api.useMutation('post', '/crossroads/logging/bulk_retry', {
    retry: 0, // Do not retry, this could start a second bulk retry operation unintentionally.
    onError: (error) => {
      message.error(`Failed to start bulk retry: ${JSON.stringify(error)}`)
    },
  })
}
